import { z } from 'zod/v4';
import {
  DEFAULT_PAGE_SIZE,
  FILTER_MAX_LENGTH,
  MAX_PAGE_SIZE,
} from '../constants.js';
import { QuestionnaireRequestStatisticsSchema } from './questionnaire-request-list.js';
import { QUESTIONNAIRE_REQUEST_STATUSES } from './questionnaire-request-status.js';

/**
 * Sort columns the by-company request list accepts — mirrors
 * `SORT_COLUMNS_ENUM_BY_COMPANY_ID` in the legacy
 * `routes/schemas/v1/request/request.js`. Each key maps to a SQL expression in
 * `core/db/questionnaire-requests/list-by-company-filters.ts`
 * (`COMPANY_LIST_SORT_COLUMNS`); keep the two in sync.
 */
export const QuestionnaireRequestsCompanyListSortColumns = [
  'name',
  'requestedBy',
  'updatedAt',
  'createdAt',
  'about',
  'status',
  'lastReminded',
  'dueDate',
  'progress',
] as const;

export type QuestionnaireRequestsCompanyListSortColumn =
  (typeof QuestionnaireRequestsCompanyListSortColumns)[number];

/**
 * Filter columns the by-company request list accepts.
 *
 * `requestedByCompanyId` is not in the legacy `FILTER_COLUMNS_ENUM_BY_COMPANY_ID`
 * — both frontends send it as the "Requested by" dropdown's key, and the legacy
 * builder silently dropped it. It is mapped now, so the dropdown filters.
 */
export const QuestionnaireRequestsCompanyListFilterColumns = [
  'name',
  'requestedBy',
  'requestedByCompanyId',
  'about',
  'status',
  /** The search box: name, requested-by and about names as one OR group. */
  'search',
] as const;

const sortPair = `(${QuestionnaireRequestsCompanyListSortColumns.join('|')}):(ASC|DESC)`;

/**
 * `column:DIRECTION` pairs, comma-separated, uppercase direction only.
 *
 * Unlike the per-questionnaire list — whose legacy schema sat under the
 * unrecognised key `queryString` and so was never enforced — this route declared
 * its schema under `querystring`, so ajv really did reject anything outside this
 * pattern. Reproducing it exactly keeps a request that 400s today 400ing.
 */
const sortParamSchema = z
  .string()
  .regex(new RegExp(`^${sortPair}(,${sortPair})*$`))
  .optional();

export const QuestionnaireRequestsCompanyListQuerySchema = z.object({
  /**
   * When present, the list flips from "requests my company received" to
   * "requests my company sent to this company" — two different queries behind
   * one URL, as in the legacy handler.
   */
  companyId: z.string().max(512).optional(),
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  sort: sortParamSchema,
  /** JSON-encoded filter object. 512 is the legacy `maxLength`. */
  filter: z.string().max(FILTER_MAX_LENGTH).optional(),
});

export type QuestionnaireRequestsCompanyListQuery = z.infer<
  typeof QuestionnaireRequestsCompanyListQuerySchema
>;

/**
 * Row shape returned by the by-company request list. `looseObject` because the
 * legacy `request` response definition was `additionalProperties: true`; date
 * columns are deliberately left undeclared so they pass through as `Date` and
 * serialise to ISO strings.
 *
 * **This schema is the frontends' type for these rows** — it is imported by
 * `frontend-v2/src/api/questionnaires/types.ts` rather than re-declared there,
 * so a field the FE reads must be declared here or it infers as `unknown`. The
 * prose "field reads in the frontends" list this docblock used to carry was that
 * contract kept where no compiler could check it; the declarations below replace
 * it.
 *
 * Timestamps are `z.string()` because `transformQuestionnaireRequestRow`
 * ISO-normalises them before the serialiser sees them — see `toIso` there.
 *
 * `supplierConnected` / `answeredByConnected` are always `false`: neither query
 * selects them, and the legacy `buildRequest` ran both through `Boolean()`
 * regardless. Declared so the shape does not silently change if a future query
 * starts selecting them.
 */
export const QuestionnaireRequestCompanyListRowSchema = z.looseObject({
  id: z.string(),
  // ─── NOT NULL in the database ──────────────────────────────────────────────
  // `NOT NULL` on `supplier_questionnaire_service_requests` and selected by
  // every query feeding this schema, so they are declared required. They were
  // `.nullable().optional()` — looser than the database, and looser than what
  // the frontends had always assumed. `status` keeps an open value domain: the
  // column has no CHECK constraint, so the closed union the frontend used to
  // declare was an assumption about the *shape*; the value domain is closed
  // below.
  //
  // `status` is an enum, not a bare string. The column has no CHECK constraint,
  // but on this platform that is the norm — value domains are enforced in the
  // application layer on both sides, and this module already closes this one on
  // the way *in* (`UpdateQuestionnaireRequestStatusBodySchema`). Closing it on
  // the way out makes one definition serve the write path, the read path and
  // both frontends.
  //
  // The enum carries all five values including `DELETED`, because that is the
  // column's real domain. The lists cannot return it — every `DELETED` row in
  // staging has `is_removed = 1`, and both queries filter `is_removed = 0`, so
  // the visible set is the four the frontends label. Declaring only four would
  // make the type describe the query rather than the column.
  //
  // `name` is `supplier_questionnaires.name` — also `NOT NULL`, and reached
  // through an inner `JOIN` (not `LEFT JOIN`), so it is always present.
  questionnaireId: z.string(),
  name: z.string(),
  forCompanyId: z.string(),
  onBehalfOfCompanyId: z.string(),
  requestedBy: z.string().nullable().optional(),
  about: z.string().nullable().optional(),
  status: z.enum(QUESTIONNAIRE_REQUEST_STATUSES),
  updatedByUserName: z.string().nullable().optional(),
  updatedByCompanyName: z.string().nullable().optional(),
  supplierConnected: z.boolean().nullable().optional(),
  answeredByConnected: z.boolean().nullable().optional(),
  dueDate: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  lastReminded: z.string().nullable().optional(),
  statistics: QuestionnaireRequestStatisticsSchema.nullable().optional(),
});

export type QuestionnaireRequestCompanyListRow = z.infer<
  typeof QuestionnaireRequestCompanyListRowSchema
>;

/**
 * The rows of the answer requests list.
 *
 * Just the array — the `{metadata, data}` envelope around it comes from
 * `buildResponseSchemas({ 200: { type: 'data', schema, paginated: true } })` on the
 * route, the same builder every other paginated route on the platform uses. Nothing
 * here describes the envelope, so there is one definition of it rather than six.
 */
export const QuestionnaireRequestsCompanyListResponseSchema = z.array(
  QuestionnaireRequestCompanyListRowSchema,
);

export type QuestionnaireRequestsCompanyListResponse = z.infer<
  typeof QuestionnaireRequestsCompanyListResponseSchema
>;
